import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ChatCircleText, Phone, PaperPlaneTilt, SpinnerGap, Robot, House, User as UserIcon } from '@phosphor-icons/react';

export default function MessagingPage() {
  const { api } = useAuth();
  const [status, setStatus] = useState(null);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [activeProp, setActiveProp] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [calls, setCalls] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('sms');

  useEffect(() => {
    api.get('/twilio/status').then(({ data }) => {
      setStatus(data);
      if (data.properties?.length) setActiveProp(data.properties[0].id);
    });
    api.get('/twilio/available-numbers').then(({ data }) => setAvailableNumbers(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeProp) return;
    Promise.all([
      api.get(`/properties/${activeProp}/sms-conversations`),
      api.get(`/properties/${activeProp}/call-logs`),
    ]).then(([c, cl]) => {
      setConversations(c.data);
      setCalls(cl.data);
      setSelectedConvo(c.data[0] || null);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProp]);

  const assignNumber = async (propId, number) => {
    await api.post(`/properties/${propId}/twilio-number`, { twilio_phone_number: number });
    const { data } = await api.get('/twilio/status');
    setStatus(data);
  };

  const sendReply = async () => {
    if (!reply.trim() || !selectedConvo) return;
    setSending(true);
    try {
      await api.post(`/properties/${activeProp}/send-sms`, { to: selectedConvo.guest_phone, body: reply });
      setReply('');
      const { data } = await api.get(`/properties/${activeProp}/sms-conversations`);
      setConversations(data);
      const refreshed = data.find(c => c.guest_phone === selectedConvo.guest_phone);
      if (refreshed) setSelectedConvo(refreshed);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (!status) return <p className="text-sm text-zinc-500">Loading...</p>;

  return (
    <div data-testid="messaging-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Messaging</h1>
        <p className="text-sm text-zinc-500 mt-1">AI-powered SMS & Voice concierge — conversations handled automatically, 24/7.</p>
      </div>

      {!status.configured && (
        <div className="mb-4 px-4 py-3 text-sm rounded-sm border bg-amber-50 border-amber-200 text-amber-800" data-testid="twilio-not-configured">
          Twilio isn't configured on the platform yet. Contact support to enable SMS/Voice agents.
        </div>
      )}

      {/* Number assignment strip */}
      <div className="bg-white border border-zinc-200 p-4 mb-4" data-testid="twilio-config-panel">
        <p className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-3">Assign a Twilio number per property</p>
        <div className="space-y-2">
          {status.properties.map((p) => (
            <div key={p.id} className="flex items-center gap-3" data-testid={`prop-number-row-${p.id}`}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <House size={14} weight="duotone" className="text-zinc-400 shrink-0" />
                <span className="text-sm font-bold text-zinc-900 truncate">{p.name}</span>
              </div>
              <select
                value={p.twilio_phone_number || ''}
                onChange={(e) => assignNumber(p.id, e.target.value)}
                className="px-3 py-1.5 border border-zinc-200 text-sm rounded-sm bg-white"
                data-testid={`assign-number-${p.id}`}
              >
                <option value="">— No number —</option>
                {availableNumbers.map((n) => (
                  <option key={n.sid} value={n.phone_number}>{n.friendly_name} ({n.phone_number})</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        {availableNumbers.length === 0 && status.configured && (
          <p className="text-xs text-zinc-500 mt-3">No phone numbers found in your Twilio account. Buy one in the Twilio Console, then refresh this page.</p>
        )}
      </div>

      {/* Property selector */}
      <div className="flex items-center gap-3 mb-4">
        <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">Property</label>
        <select value={activeProp || ''} onChange={(e) => setActiveProp(e.target.value)} className="px-3 py-2 border border-zinc-200 text-sm rounded-sm bg-white" data-testid="messaging-property-picker">
          {status.properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* Tab switcher */}
      <div className="flex border-b border-zinc-200 mb-4">
        <button onClick={() => setTab('sms')} className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${tab === 'sms' ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-zinc-500 hover:text-zinc-900'}`} data-testid="tab-sms">
          <ChatCircleText size={14} className="inline mr-1.5" /> SMS ({conversations.length})
        </button>
        <button onClick={() => setTab('voice')} className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px ${tab === 'voice' ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-zinc-500 hover:text-zinc-900'}`} data-testid="tab-voice">
          <Phone size={14} className="inline mr-1.5" /> Calls ({calls.length})
        </button>
      </div>

      {tab === 'sms' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white border border-zinc-200">
          <div className="border-r border-zinc-200 max-h-[60vh] overflow-y-auto" data-testid="sms-list">
            {conversations.length === 0 ? (
              <p className="p-6 text-sm text-zinc-500">No SMS conversations yet. Once a guest texts the assigned Twilio number, it'll appear here.</p>
            ) : conversations.map((c) => (
              <button key={c.guest_phone} onClick={() => setSelectedConvo(c)} className={`w-full text-left p-3 border-b border-zinc-100 hover:bg-zinc-50 ${selectedConvo?.guest_phone === c.guest_phone ? 'bg-blue-50' : ''}`} data-testid={`convo-${c.guest_phone}`}>
                <p className="text-sm font-bold text-zinc-900">{c.guest_phone}</p>
                <p className="text-xs text-zinc-500 truncate mt-0.5">{c.messages?.slice(-1)?.[0]?.body || '—'}</p>
                <p className="text-[10px] text-zinc-400 mt-1">{c.last_message_at?.slice(0, 16).replace('T', ' ')}</p>
              </button>
            ))}
          </div>
          <div className="md:col-span-2 flex flex-col max-h-[60vh]">
            {selectedConvo ? (
              <>
                <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-zinc-900">{selectedConvo.guest_phone}</p>
                    <p className="text-xs text-zinc-500">{selectedConvo.messages?.length || 0} messages</p>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="sms-thread">
                  {selectedConvo.messages?.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'guest' ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-sm text-sm ${m.role === 'guest' ? 'bg-zinc-100 text-zinc-900' : m.role === 'ai' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'}`}>
                        <div className="flex items-center gap-1 mb-0.5">
                          {m.role === 'guest' ? <UserIcon size={10} /> : m.role === 'ai' ? <Robot size={10} /> : <UserIcon size={10} />}
                          <span className="text-[10px] font-bold uppercase opacity-80">{m.role}</span>
                          {m.tag === 'review_request' && <span className="text-[9px] bg-white/20 px-1 rounded">★ review</span>}
                        </div>
                        <p>{m.body}</p>
                        <p className="text-[10px] opacity-60 mt-1">{m.timestamp?.slice(11, 16)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-zinc-200 flex gap-2">
                  <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && sendReply()} placeholder="Reply as owner..." className="flex-1 px-3 py-2 border border-zinc-200 text-sm rounded-sm" data-testid="sms-reply-input" />
                  <button onClick={sendReply} disabled={sending || !reply.trim()} className="px-4 py-2 bg-[#0052FF] text-white text-sm font-bold rounded-sm hover:bg-[#003EDD] disabled:opacity-50 flex items-center gap-1.5" data-testid="sms-send-btn">
                    {sending ? <SpinnerGap size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />} Send
                  </button>
                </div>
              </>
            ) : (
              <p className="p-6 text-sm text-zinc-500">Select a conversation.</p>
            )}
          </div>
        </div>
      )}

      {tab === 'voice' && (
        <div className="bg-white border border-zinc-200" data-testid="voice-calls">
          {calls.length === 0 ? (
            <p className="p-6 text-sm text-zinc-500">No calls yet. Incoming calls to assigned Twilio numbers will appear here with AI transcripts.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left">
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">From</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Started</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Duration</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-xs font-bold text-zinc-500 uppercase tracking-wide">Transcript preview</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {calls.map((c, i) => (
                  <tr key={c.id || i} data-testid={`call-row-${i}`}>
                    <td className="px-4 py-3 font-bold text-zinc-900">{c.guest_phone}</td>
                    <td className="px-4 py-3 text-zinc-600">{c.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-3 text-zinc-600">{c.duration_seconds || 0}s</td>
                    <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 font-bold bg-zinc-100 text-zinc-700 rounded-sm">{c.status}</span></td>
                    <td className="px-4 py-3 text-zinc-600 text-xs max-w-xs truncate">
                      {(c.transcript || []).slice(-1)[0]?.text || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
